import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty, orderBy, isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'

import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import DoFormsFormTile from '../../doforms-form/DoformsFormTile'
import { Shortcut as ShortcutIcon } from '@mui/icons-material'
import moment from 'moment'

import { Scheduler } from '@aldabil/react-scheduler'
import { Typography } from '@mui/material'

import LoadingSpinner from 'custom-components/LoadingSpinner'
import { SchedulerCustomViewer, useStyles } from './custom-components/SchedulerCustomViewer'
import { CustomEditor } from '../../dispatch-scheduler/DispatchSchedulerCustomEditor'
import DoformsSendDispatch from 'components/data/dispatch/DoformsSendDispatch'
import ToastAlert from 'components/core/Layouts/ToastAlert'
import { useModalDashboardDialog } from '../../modal-dashboard/ModalDashboard'

import {
	getDevices,
	getDispatchSettings,
	updateAppointments,
} from 'components/core/services/environmentService'

import { ENV_ACTIONS } from 'reducers/environmentReducer'
import { useEmployeeEvent } from './hook/useEmployeeEvents'
import { SUMMARY_TYPES } from '../utils/constant'
import { useSummaryDefaultProjectFormInfo } from '../utils/hooks/summaryHeaderQuery'

import { EVENT_STATUS } from '../../dispatch-scheduler/DispatchSchedulerTile'
import { ACTION_TYPE } from '../../dispatch-scheduler/helper/constants'
import { LoadingButton } from '@mui/lab'
import { isNullOrUndefined } from 'utils/functions/helpers'
import { getDefaultMigratedOptionValue } from '../../utils/tileOtherOptionMigration'
import { checkKeyIsExist } from '../../utils/helpers'

export function EmployeeRightPanel(props) {
	const {
		tile,
		selectedMobile,
		settings,
		initLoading,
		parentPassedIdValue,
		parentModalDashboardSetting,
		linkedMobileNumber,
	} = props
	const { environment } = useSelector((state) => state)
	const [t] = useTranslation()
	const dispatch = useDispatch()

	const {
		projectFormInfo,
		schedulerRangeType,
		schedulerStartTime,
		schedulerEndTime,
		schedulerColorStatusSettings,
		otherOptionsChecked,
		migrateOtherOptions,
		modalDashboardSetting: mainModalDashboardSetting,
		mapModalDashboardSetting: mainMapModalDashboardSetting,
	} = useMemo(() => settings, [settings])

	// default view hook
	const { selectedNewTaskForm } = useSummaryDefaultProjectFormInfo({
		summaryType: SUMMARY_TYPES.EMPLOYEE,
	})

	// dialog state
	const [openDialog, setOpenDialog] = useState(false)
	const [titleLabel, setTitleLabel] = useState('')
	const [displayProjectFormInfo, setDisplayProjectFormInfo] = useState(null)

	function buildTitleLabel(projectFormInfo) {
		if (!projectFormInfo) return ''
		return `${projectFormInfo.projectName} / ${projectFormInfo.formName}`
	}

	function handleOpenDialog(projectFormInfo) {
		setOpenDialog(true)
		setTitleLabel(buildTitleLabel(projectFormInfo))
		setDisplayProjectFormInfo(projectFormInfo)
	}

	function handleCloseDialog(shouldUseInterval = false) {
		setOpenDialog(false)
		setDisplayProjectFormInfo(null)
	}

	const [selectedModalDashboardSetting, setSelectedModalDashboardSetting] = useState(null)
	const { handleOpenModalDashboardDialog, handleCloseModalDashboardDialog, ModalDashboard } =
		useModalDashboardDialog({
			parentPassedIdValue: parentPassedIdValue,
			parentModalDashboardSetting: parentModalDashboardSetting,
		})

	const schedulerRef = useRef(null)

	const [hourFormat, setHourFormat] = useState('12')
	const defaultDuration = '0.5'
	const [slotTime, setSlotTime] = useState('')

	const iconTheme = environment.theme.icons
	const classes = useStyles(iconTheme)
	const [loading, setLoading] = useState(false)
	const [schedulerKey, setSchedulerKey] = useState(0)
	const [view, setView] = useState('day')
	const [isAgenda, setIsAgenda] = useState(true)
	const [isAllowDispatch, setIsAllowDispatch] = useState(false)
	const [schedulerColorStatus, setSchedulerColorStatus] = useState(schedulerColorStatusSettings)
	const [events, setEvents] = useState([])
	const [selectedDate, setSelectedDate] = useState(null)
	const [selectedDateDisplay, setSelectedDateDisplay] = useState(moment())
	const [mobileUnits, setMobileUnits] = useState([])
	const [alertInfo, setAlertInfo] = useState({
		open: false,
		text: '',
		type: 'success',
	})

	const { appoinments, isLoadingData, refetch, defaultSchedulerStatus } = useEmployeeEvent({
		tileKey: tile.key,
		rangeType: view,
		selectedDevice: selectedMobile,
		selectedDateDisplay: selectedDateDisplay,
	})

	const isScheduleLoading = useMemo(
		() => loading || isLoadingData || initLoading,
		[loading, initLoading, isLoadingData]
	)

	useEffect(() => {
		const timeFormat = environment?.userCurrent?.time?.timeFormat
		let format = '12'
		if (timeFormat && timeFormat.includes('HH')) {
			format = '24'
		}
		setHourFormat(format)
	}, [environment?.userCurrent?.time?.timeFormat])

	const formSelected = useMemo(() => {
		const allForms = environment?.forms
		const defaultFormInfo = {
			...projectFormInfo,
			key: projectFormInfo?.formKey,
			name: projectFormInfo?.formName,
		}
		if (isEmpty(allForms)) {
			return defaultFormInfo
		}

		const form = allForms.find((item) => item.key === projectFormInfo?.formKey)
		if (!form) {
			return defaultFormInfo
		}

		return {
			...form,
			projectKey: projectFormInfo?.projectKey,
		}
	}, [projectFormInfo, environment?.forms])

	useEffect(() => {
		const allowDispatch = otherOptionsChecked.some((item) => item === 'allowDispatch')
		setIsAllowDispatch(allowDispatch)
	}, [otherOptionsChecked])

	useEffect(() => {
		const applyDisplayNone = () => {
			const schedulerRoot = schedulerRef.current?.el
			if (!schedulerRoot) return

			const paper = schedulerRoot.querySelector('.MuiPaper-root')
			if (!paper) return

			const buttons = paper.querySelectorAll('button')

			buttons.forEach((btn) => {
				const text = btn.textContent?.trim()

				if (btn.getAttribute('aria-label') === 'Today') {
					btn.style.display = 'none'
				}

				if (text === 'Day') {
					btn.style.display = 'none'
				}
			})
		}
		applyDisplayNone()

		const observer = new MutationObserver(() => {
			applyDisplayNone()
		})

		observer.observe(document.body, { childList: true, subtree: true })
		return () => observer.disconnect()
	}, [selectedDateDisplay, view])

	useEffect(() => {
		const applyToolbarChanges = () => {
			const schedulerRoot = schedulerRef.current?.el
			if (!schedulerRoot) return

			const paper = schedulerRoot.querySelector('.MuiPaper-root')
			if (!paper) return

			const buttons = paper.querySelectorAll('button')
			let monthBtn = null
			let weekBtn = null

			buttons.forEach((btn) => {
				const text = btn.textContent?.trim()

				if (text === 'Month') monthBtn = btn
				if (text === 'Week') weekBtn = btn
			})
			if (monthBtn && weekBtn) {
				const parent = monthBtn.parentNode
				if (parent && weekBtn.nextSibling !== monthBtn) {
					parent.insertBefore(weekBtn, monthBtn)
				}
			}
		}
		applyToolbarChanges()

		const observer = new MutationObserver(() => {
			applyToolbarChanges()
		})

		observer.observe(document.body, { childList: true, subtree: true })
		return () => observer.disconnect()
	}, [selectedDateDisplay, view])

	useEffect(() => {
		if (schedulerRef.current?.scheduler) {
			if (schedulerRef.current.scheduler.agenda !== isAgenda) {
				schedulerRef.current.scheduler.handleState(isAgenda, 'agenda')
			}
		}
	}, [schedulerKey])

	useEffect(() => {
		const showAgenda = !migrateOtherOptions?.includes('showAgenda')
			? getDefaultMigratedOptionValue({
					key: 'showAgenda',
					tileType: tile.type,
			  })
			: checkKeyIsExist('showAgenda', otherOptionsChecked)

		setIsAgenda(showAgenda)

		// Sync scheduler internal state
		const scheduler = schedulerRef.current?.scheduler
		if (scheduler && scheduler.agenda !== showAgenda) {
			scheduler.handleState(showAgenda, 'agenda')

			if (showAgenda) {
				scheduler.handleState('day', 'view')
			}
		}

		// Sync React state
		setView(showAgenda ? 'day' : schedulerRangeType)

		// Force scheduler re-mount when agenda changes
		setSchedulerKey((prevKey) => prevKey + 1)
	}, [otherOptionsChecked, migrateOtherOptions, schedulerRangeType])

	useEffect(() => {
		setSchedulerColorStatus(schedulerColorStatusSettings)
		setSchedulerKey((prevKey) => prevKey + 1)
	}, [schedulerColorStatusSettings])

	function getColorStatusDispatch(status, defaultSchedulerStatus) {
		if (!defaultSchedulerStatus) return ''
		const statusColor = defaultSchedulerStatus.find((item) => item.label === status)
		if (!statusColor) return ''
		if (statusColor.color.includes('#')) return statusColor.color
		return `#${statusColor.color}`
	}

	function getActionsDispatch(status) {
		const actions = [ACTION_TYPE.COPY, ACTION_TYPE.VIEW, ACTION_TYPE.HISTORY]
		if (
			status === EVENT_STATUS.PENDING ||
			status === EVENT_STATUS.SCHEDULED ||
			status === EVENT_STATUS.REJECTED ||
			status === EVENT_STATUS.RECALLED ||
			status === EVENT_STATUS.ACKNOWLEDGED ||
			status === EVENT_STATUS.COMPLETED
		) {
			actions.push(ACTION_TYPE.EDIT)
			actions.push(ACTION_TYPE.DELETE)
		}
		if (
			status === EVENT_STATUS.SENT ||
			status === EVENT_STATUS.EMAILED ||
			status === EVENT_STATUS.RECEIVED ||
			status === EVENT_STATUS.VIEWED ||
			status === EVENT_STATUS.OPENED
		) {
			actions.push(ACTION_TYPE.RECALL)
		}
		if (status === EVENT_STATUS.PENDING || status === EVENT_STATUS.SCHEDULED) {
			actions.push(ACTION_TYPE.DRAGDROP)
		}
		if (
			status === EVENT_STATUS.SCHEDULED ||
			status === EVENT_STATUS.SENT ||
			status === EVENT_STATUS.EMAILED
		) {
			actions.push(ACTION_TYPE.SENDSMSMAIL)
		}

		return actions
	}

	useEffect(() => {
		if (!appoinments) return

		let eventList = []
		for (let i = 0; i < appoinments.data.length; i++) {
			const currentData = appoinments.data[i]
			const startMoment = moment(`${currentData.date} ${currentData.time}`, 'YYYY-MM-DD HH:mm')
			const endMoment = startMoment.clone().add(currentData.duration, 'minutes')
			const actions = getActionsDispatch(currentData?.dispatch?.systemStatus?.toLowerCase())
			eventList.push({
				index: i,
				event_id: currentData.key,
				agendaView: false,
				title: isEmpty(currentData?.dispatch?.name)
					? currentData?.dispatch?.formName
					: currentData?.dispatch?.name,
				start: startMoment.toDate(),
				end: endMoment.toDate(),
				dispatch: currentData.dispatch,
				deletable: false,
				editable: true,
				nickName: isEmpty(currentData?.dispatch?.ownerName)
					? currentData?.dispatch?.ownerId
					: currentData?.dispatch?.ownerName,
				color: getColorStatusDispatch(
					currentData?.dispatch?.systemStatus,
					schedulerColorStatus || defaultSchedulerStatus
				),
				draggable: actions.includes(ACTION_TYPE.DRAGDROP),
				availableActions: actions,
			})
		}
		setEvents(orderBy(eventList, (event) => event.start))
	}, [appoinments, schedulerColorStatus])

	const displayEvents = useMemo(() => {
		if (view === 'day' && !isAgenda) {
			const eventsMap = {}
			events.forEach((event) => {
				const hour = moment(event.start).format('HH')
				const currentValue = eventsMap[hour]
				if (!currentValue) {
					eventsMap[hour] = [event]
					return
				}
				eventsMap[hour] = [...currentValue, event]
			})
			if (isEmpty(eventsMap)) return []

			const eventWithMoreButton = Object.values(eventsMap)
				.map((eventsByHour) => {
					if (eventsByHour.length >= 4) {
						const total = eventsByHour.length - 3
						const lastEvent = eventsByHour[eventsByHour.length - 1]
						const eventButton = {
							title: `${total} More`,
							start: lastEvent.start,
							end: lastEvent.end,
							index: lastEvent.index,
							event_id: lastEvent.event_id + '_moreBtn',
							deletable: false,
							editable: false,
							draggable: false,
							status: '',
							onClick: () => {
								setIsAgenda(true)
								setView('day')
								const hour = moment(eventsByHour[0].start).format('HH')
								setSlotTime(hour)
								schedulerRef?.current?.scheduler?.handleState(true, 'agenda')
							},
						}
						const result = eventsByHour.slice(0, 3)
						result.push(eventButton)
						return result
					}
					return eventsByHour
				})
				.flat()

			return eventWithMoreButton
		}

		if (view === 'day' && isAgenda && !isEmpty(slotTime)) {
			const filteredEvents = events.filter((event) => moment(event.start).format('HH') === slotTime)
			return filteredEvents
		}
		events.forEach((event) => {
			event.agendaView = isAgenda
		})
		return events
	}, [isAgenda, view, events])

	const handleEventAdd = (newEvent) => {
		setEvents([...events, newEvent])
	}

	const handleEventUpdate = (updatedEvent) => {
		const updatedEvents = events.map((event) =>
			event.event_id === updatedEvent.event_id ? updatedEvent : event
		)
		setEvents(updatedEvents)
	}

	const handleEventDelete = (eventId) => {
		const updatedEvents = events.filter((event) => event.event_id !== eventId)
		setEvents(updatedEvents)
	}

	const handleViewChange = (nextView, nextAgenda) => {
		const currentAgenda = schedulerRef.current?.scheduler?.agenda
		const agendaClicked = view === nextView && currentAgenda !== nextAgenda

		if ((nextView === 'day' && !nextAgenda) || agendaClicked) {
			setView('day')
			setIsAgenda(true)

			schedulerRef.current?.scheduler?.handleState('day', 'view')
			schedulerRef.current?.scheduler?.handleState(true, 'agenda')
			setSchedulerKey((prev) => prev + 1)
			return
		}

		setView(nextView)
		setIsAgenda(false)

		schedulerRef.current?.scheduler?.handleState(nextView, 'view')
		schedulerRef.current?.scheduler?.handleState(false, 'agenda')
	}

	const handleCellClick = (source) => {
		if (checkNotValidDateTime(source)) return
		if (view !== 'month') {
			setSelectedDate(moment(source).format('YYYY-MM-DDTHH:mm:ss'))
		} else {
			const sourceMoment = moment(source)
			const momentToday = moment().clone()
			if (momentToday.isBefore(sourceMoment, 'day')) {
				const selectMoment = sourceMoment
					.clone()
					.startOf('day')
					.add(8, 'hours')
					.format('YYYY-MM-DDTHH:mm:ss')
				setSelectedDate(selectMoment)
			} else {
				const currentMinute = momentToday.clone().minutes()
				let selectMoment
				if (currentMinute < 30) {
					selectMoment = momentToday.clone().startOf('hour').add(30, 'minutes')
				} else {
					selectMoment = momentToday
						.clone()
						.add(1, 'hour')
						.set({ minute: 0, second: 0, millisecond: 0 })
				}
				setSelectedDate(selectMoment)
			}
		}
		dispatch({
			type: ENV_ACTIONS.SHOW_SEND_DISPATCH,
			payload: 'dashboard',
		})
	}

	const checkNotValidDateTime = (date) => {
		const isNotValid = moment(date).isBefore(moment(), view !== 'month' ? null : 'day')
		if (isNotValid) {
			setAlertInfo((prev) => ({
				...prev,
				open: true,
				type: 'error',
				text: 'You can not schedule dispatch in the past',
			}))
		}
		return isNotValid
	}
	const handleEventDrop = async (event, droppedOn, updatedEvent, originalEvent) => {
		if (checkNotValidDateTime(droppedOn)) return

		const sourceMoment = moment(droppedOn)
		let selectMoment
		if (view !== 'month') {
			selectMoment = sourceMoment.clone()
		} else {
			const momentToday = moment().clone()
			if (momentToday.isBefore(sourceMoment, 'day')) {
				selectMoment = sourceMoment.clone().startOf('day').add(8, 'hours')
			} else {
				const currentMinute = momentToday.clone().minutes()
				if (currentMinute < 30) {
					selectMoment = momentToday.clone().startOf('hour').add(30, 'minutes')
				} else {
					selectMoment = momentToday
						.clone()
						.add(1, 'hour')
						.set({ minute: 0, second: 0, millisecond: 0 })
				}
			}
		}
		const updatedEvents = events.map((event) =>
			event.event_id === updatedEvent.event_id ? updatedEvent : event
		)

		try {
			setLoading(true)
			const res = await updateAppointments(environment.apiToken, updatedEvent.event_id, {
				date: selectMoment.format('YYYY-MM-DD'),
				time: selectMoment.format('HH:mm'),
			})
			if (res.status === 200) {
				setEvents(updatedEvents)
			}
			setLoading(false)
		} catch (err) {
			const errorMessage = 'Code ' + err.response?.data?.code + ': ' + err.response?.data?.message
			setAlertInfo((prev) => ({
				...prev,
				open: true,
				type: 'error',
				text: errorMessage,
			}))
			setLoading(false)
		}
	}

	useEffect(() => {
		setSchedulerKey((prev) => prev + 1)
	}, [isScheduleLoading])

	useEffect(() => {
		setSchedulerKey((prev) => prev + 1)
	}, [displayEvents])

	useEffect(() => {
		setSchedulerKey((prevKey) => prevKey + 1)
	}, [defaultSchedulerStatus])

	const [selectedEvent, setSelectedEvent] = useState(null)
	const [openCustomEditor, setOpenCustomEditor] = useState(false)

	const [action, setAction] = useState('')

	// Memoized CustomViewer for Agenda view
	const memoizedCustomViewer = useCallback(
		() =>
			isAgenda &&
			selectedEvent && (
				<SchedulerCustomViewer
					isFromScheduler={false}
					selectedEvent={selectedEvent}
					setSelectedEvent={setSelectedEvent}
					event={selectedEvent}
					close={() => setSelectedEvent(null)}
					open={!!selectedEvent}
					hourFormat={hourFormat}
					mobileUnits={mobileUnits}
					setAlertInfo={setAlertInfo}
					setAction={setAction}
					setOpenCustomEditor={setOpenCustomEditor}
					refetchAppointments={refetch}
				/>
			),
		[isAgenda, selectedEvent, hourFormat, mobileUnits]
	)

	return (
		<div style={{ paddingLeft: '15px' }}>
			<ToastAlert alertInfo={alertInfo} setAlertInfo={setAlertInfo} />

			<div>
				<div id={'headerEmployeeTile'} style={{ marginRight: '5px' }}>
					<Grid
						container
						spacing={1}
						alignItems="center"
						paddingLeft="8px"
						paddingBottom="5px"
						paddingTop="5px"
					>
						{isAllowDispatch && (
							<Grid item xs={1} md={1} lg={1}>
								<div style={{ display: 'flex', justifyContent: 'start' }}>
									<LoadingButton
										id={'send-dispatch'}
										className={classes.icon}
										loadingPosition="start"
										onClick={(e) => {
											dispatch({
												type: ENV_ACTIONS.SHOW_SEND_DISPATCH,
												payload: 'dashboard',
											})
										}}
										startIcon={<ShortcutIcon />}
									>
										{t('common:filters.dispatch')}
									</LoadingButton>
								</div>
							</Grid>
						)}

						<Grid item xs md lg></Grid>
						<Grid item xs={2} md={2} lg={2}>
							<Button
								fullWidth
								variant="contained"
								size="small"
								onClick={() => handleOpenDialog(selectedNewTaskForm)}
								sx={{
									height: '40px',
									textTransform: 'none',
								}}
							>
								+ {t('common:misc.summaryTask')}
							</Button>
						</Grid>
						{settings?.mapModalDashboardSetting && (
							<Grid item xs={2} md={2} lg={2}>
								<Button
									fullWidth
									variant="contained"
									size="small"
									sx={{
										height: '40px',
										textTransform: 'none',
										backgroundColor: '#B70E0E',
										':hover': {
											backgroundColor: '#b10314',
										},
										color: '#FFFFFF',
									}}
									onClick={() => {
										handleOpenModalDashboardDialog({
											passedIdValue: linkedMobileNumber,
											modalDashboardSetting: mainMapModalDashboardSetting,
										})
									}}
								>
									{t('common:misc.map')}
								</Button>
							</Grid>
						)}
						{settings?.modalDashboardSetting && (
							<Grid item xs={2} md={2} lg={2}>
								<Button
									fullWidth
									variant="contained"
									size="small"
									sx={{
										height: '40px',
										textTransform: 'none',
										backgroundColor: '#B70E0E',
										':hover': {
											backgroundColor: '#b10314',
										},
										color: '#FFFFFF',
									}}
									onClick={() => {
										handleOpenModalDashboardDialog({
											passedIdValue: linkedMobileNumber,
											modalDashboardSetting: mainModalDashboardSetting,
										})
									}}
								>
									Summary
								</Button>
							</Grid>
						)}
					</Grid>
				</div>

				<Scheduler
					key={schedulerKey}
					ref={schedulerRef}
					view={view}
					alwaysShowAgendaDays={true}
					events={displayEvents}
					hourFormat={hourFormat}
					selectedDate={selectedDateDisplay}
					loading={isScheduleLoading}
					loadingComponent={<CustomLoader />}
					onViewChange={(view, agenda) => {
						handleViewChange(view, agenda)
					}}
					onEventAdd={handleEventAdd}
					onEventUpdate={handleEventUpdate}
					onEventDelete={handleEventDelete}
					// onCellClick={handleCellClick}
					onEventDrop={(event, droppedOn, updatedEvent, originalEvent) => {
						handleEventDrop(event, droppedOn, updatedEvent, originalEvent)
					}}
					onSelectedDateChange={(date) => {
						setSlotTime('')
						setSelectedDateDisplay(date)
					}}
					day={{
						startHour: schedulerStartTime || 8,
						endHour: schedulerStartTime || 8,
					}}
					week={{
						startHour: schedulerStartTime || 8,
						endHour: schedulerEndTime || 18,
						weekStartOn: 0,
					}}
					month={{
						weekStartOn: 0,
					}}
					editable={false}
					draggable={true}
					customViewer={(event, close) => {
						return (
							<SchedulerCustomViewer
								isFromScheduler={true}
								selectedEvent={selectedEvent}
								setSelectedEvent={setSelectedEvent}
								event={event}
								close={() => {
									close()
									setSelectedEvent(null)
								}}
								open={true}
								hourFormat={hourFormat}
								mobileUnits={mobileUnits}
								setAlertInfo={setAlertInfo}
								setAction={setAction}
								setOpenCustomEditor={setOpenCustomEditor}
								refetchAppointments={refetch}
							/>
						)
					}}
					eventRenderer={({ event, ...props }) => {
						if (event.onClick) {
							return (
								<div
									style={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										height: '100%',
										width: '100%',
										background: event.color,
										cursor: 'pointer',
									}}
									onClick={() => {
										event.onClick()
									}}
								>
									<Typography variant="body2" color="primary">
										{event.title}
									</Typography>
								</div>
							)
						}

						return (
							<div
								{...props}
								style={{
									height: '-webkit-fill-available',
									width: '-webkit-fill-available',
									background: `#000000`,
								}}
								onClick={(e) => {
									props.onClick(e)
									if (!isEqual(event, selectedEvent)) {
										setSelectedEvent(event)
									}
								}}
							>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'space-between',
										height: '-webkit-fill-available',
										width: '-webkit-fill-available',
										background: event.color,
										whiteSpace: 'pre-line',
									}}
								>
									<Typography
										className={classes.eventTypographyCenter}
										variant="h6"
										fontSize="12px"
										padding="6px 6px"
										color="white"
										whiteSpace="pre-line"
									>
										{event.title}
									</Typography>
									{event.nickName && (
										<div className={classes.eventTypographyCenter}>
											<Typography
												className={classes.eventTypographyCenter}
												variant="h6"
												fontSize="12px"
												padding="6px 6px"
												color="white"
												whiteSpace="pre-line"
											>
												{event.nickName}
											</Typography>
										</div>
									)}
								</div>
								{event.agendaView && (
									<div
										style={{
											display: 'flex',
											flexDirection: 'row',
											justifyContent: 'space-between',
											height: '-webkit-fill-available',
											width: '-webkit-fill-available',
											background: event.color,
											whiteSpace: 'pre-line',
											marginTop: '-10px',
										}}
									>
										<Typography
											variant="body"
											fontSize="12px"
											padding="6px 6px"
											color="white"
											whiteSpace="pre-line"
										>
											{moment(event.start).format(hourFormat === '12' ? 'hh:mm A' : 'HH:mm')} -{' '}
											{moment(event.end).format(hourFormat === '12' ? 'hh:mm A' : 'HH:mm')}
										</Typography>
									</div>
								)}
							</div>
						)
					}}
				/>
			</div>

			{memoizedCustomViewer()}

			{action && selectedEvent && formSelected?.key && (
				<CustomEditor
					formSelected={formSelected}
					openCustomEditor={openCustomEditor}
					action={action}
					event={selectedEvent}
					close={() => {
						setAction('')
						setOpenCustomEditor(false)
					}}
					refreshData={refetch}
				/>
			)}

			{mainModalDashboardSetting && <ModalDashboard />}

			<Dialog
				fullWidth
				maxWidth="md"
				aria-labelledby="transition-modal-title"
				aria-describedby="transition-modal-description"
				open={openDialog}
				closeAfterTransition
				PaperProps={{ className: classes.dialogPaper }}
			>
				<DialogTitle
					id="record-dialog-title"
					sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
				>
					<div>{titleLabel}</div>
					<div>
						<IconButton onClick={() => handleCloseDialog()} aria-label="close">
							<CloseIcon />
						</IconButton>
					</div>
				</DialogTitle>

				<DialogContent sx={{ height: 'calc(90vh - 100px)' }}>
					{displayProjectFormInfo && (
						<DoFormsFormTile
							tile={{ ...tile, isShowDialog: true }}
							openTileFrmGrid={true}
							initProjectFormInfo={displayProjectFormInfo}
							setIsOpenDialog={handleCloseDialog}
							useSettings={false}
							handleChangeTitle={(projectFormInfo) => {
								const newTitleLabel = buildTitleLabel(projectFormInfo)
								if (isEqual(titleLabel, newTitleLabel)) return
								setTitleLabel(newTitleLabel)
							}}
						/>
					)}
				</DialogContent>
			</Dialog>

			<DoformsSendDispatch
				onDialogOpen={handleCellClick}
				formSelected={formSelected}
				action={'send'}
				title={t('common:tabs.dispatchScheduler')}
				recordsLoading={false}
				source={'dashboard'}
				tab={'forms'}
				environment={environment}
				deviceSelected={selectedMobile}
				selectedDate={selectedDate || null}
				duration={defaultDuration}
				refreshData={refetch}
			/>
		</div>
	)
}

const CustomLoader = () => {
	return (
		<div style={{ textAlign: 'center', padding: '20px' }}>
			<LoadingSpinner />
		</div>
	)
}
